import {
  Bot,
  Globe2,
  LayoutDashboard,
  BarChart3,
  Map as MapIcon,
  CalendarClock,
  Boxes,
  Clapperboard,
  type LucideIcon,
} from "lucide-react";

/**
 * services.ts — single source of truth for the service catalog.
 *
 * Consumed by the home "What we build" grid, /services, and the nav
 * MegaMenu, so a title or slug can never drift between surfaces. Slugs
 * must match the keys in pages/ServiceDetail.tsx.
 *
 * Order is deliberate: AI agents + workflow integration leads because
 * it is the studio's headline offer; the specialty real-estate line
 * closes the list.
 */
export type ServiceEntry = {
  slug: string;
  /** Short nav label (MegaMenu, home grid). */
  name: string;
  /** Full title (/services card heading, JSON-LD Service name). */
  title: string;
  /** One-line nav description. */
  short: string;
  /** Card body copy. */
  line: string;
  stack: string[];
  icon: LucideIcon;
};

export const SERVICES: ServiceEntry[] = [
  {
    slug: "ai-features",
    name: "AI agents & workflows",
    title: "AI agents & workflow integration",
    short: "Agents wired into the tools you run",
    line: "Claude-powered agents and copilots wired into your existing systems — tool-use, retrieval, guardrails, and a full audit trail.",
    stack: ["Anthropic", "Bedrock", "Tool-use", "RAG"],
    icon: Bot,
  },
  {
    slug: "custom-systems",
    name: "Custom software",
    title: "Bespoke business systems",
    short: "When off-the-shelf breaks down",
    line: "When off-the-shelf stacks break down, we design, build, and run the system that fits how you actually operate.",
    stack: ["TypeScript", "CDK", "Serverless", "Observability"],
    icon: Boxes,
  },
  {
    slug: "portals",
    name: "Client portals",
    title: "Enterprise web portals",
    short: "Auth, billing, audit logs",
    line: "Role-aware portals with audit logs, files, billing, and thread-scoped messaging your clients actually log into.",
    stack: ["React", "Cognito", "DynamoDB", "SES"],
    icon: LayoutDashboard,
  },
  {
    slug: "dashboards",
    name: "Ops dashboards",
    title: "Executive & operator dashboards",
    short: "Information density over decoration",
    line: "Keyboard-first dashboards with query budgets — fast, dense, and fit for Monday morning.",
    stack: ["React", "TanStack", "API GW", "Aurora"],
    icon: BarChart3,
  },
  {
    slug: "marketing-sites",
    name: "Marketing sites",
    title: "Premium marketing websites",
    short: "Premium pages under 1s LCP",
    line: "Fast, search-ready sites — structured data, Core Web Vitals budgets, and motion used with restraint.",
    stack: ["React", "Vite", "Tailwind", "CloudFront"],
    icon: Globe2,
  },
  {
    slug: "payroll",
    name: "Payroll & workforce",
    title: "Payroll & workforce workflows",
    short: "From 3-day close to 4 hours",
    line: "Time-cards, approvals, overtime, and exports your accountant accepts on the first try.",
    stack: ["React", "Lambda", "DynamoDB", "Step Functions"],
    icon: CalendarClock,
  },
  {
    slug: "mapping",
    name: "Geospatial & mapping",
    title: "Geospatial & mapping platforms",
    short: "Mapbox + globe projections",
    line: "Map-first platforms with server-shaped tiles, globe views, and layered analytics.",
    stack: ["Mapbox GL", "Lambda", "S3", "Athena"],
    icon: MapIcon,
  },
  {
    slug: "real-estate",
    name: "Real estate media",
    title: "Real estate listing films & property sites",
    short: "8K listing films + property sites",
    line: "8K aerial film plus a branded single-property website and domain — live in 7 days.",
    stack: ["8K Drone", "Three.js", "MapLibre", "Matterport"],
    icon: Clapperboard,
  },
];
